import { useId } from "react";

import { HabitIcon } from "@/components/ui/habit-icon";
import type { ThemeName } from "@/theme/constants";

import styles from "./appearance-choices.module.scss";

const SAMPLE_HABITS = [
  { name: "Drink water", icon: "💧", color: "#8db4c2", percent: 80 },
  { name: "Read", icon: "📖", color: "#cebb82", percent: 60 },
  { name: "Meditate", icon: "🧘", color: "#bfa0b0", percent: 70 },
];
const THEMES: { value: ThemeName; label: string }[] = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "amoled", label: "AMOLED" },
  { value: "system", label: "System" },
];
const DENSITIES = [
  { value: "auto", label: "Auto" },
  { value: "lean", label: "Lean" },
  { value: "dense", label: "Dense" },
];

function SampleHabits({
  compact = false,
  theme,
}: {
  compact?: boolean;
  theme?: "light" | "dark" | "amoled";
}) {
  return (
    <div className={styles.sampleRows}>
      {SAMPLE_HABITS.map((habit) => (
        <div key={habit.name} className={styles.sampleRow}>
          <HabitIcon value={habit.icon} size={36} theme={theme} />
          <div className={styles.sampleContent}>
            {!compact && <span>{habit.name}</span>}
            <div className={styles.progress}>
              <div style={{ width: `${habit.percent}%`, backgroundColor: habit.color }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
function ThemeSample({ theme }: { theme: ThemeName }) {
  const surface = (mode: "light" | "dark" | "amoled") => (
    <div className={styles.miniApp} data-theme={mode}>
      <div className={styles.miniSidebar}>Habits</div>
      <div className={styles.miniContent}>
        <SampleHabits compact theme={mode} />
      </div>
    </div>
  );
  return (
    <div className={styles.themeSample} aria-hidden="true">
      {surface(theme === "system" ? "light" : theme)}
      {theme === "system" && <div className={styles.systemDark}>{surface("dark")}</div>}
    </div>
  );
}
export function AppearanceChoices({
  theme,
  density,
  onThemeChange,
  onDensityChange,
}: {
  theme: ThemeName;
  density: string;
  onThemeChange: (theme: ThemeName) => void;
  onDensityChange: (density: string) => void;
}) {
  const id = useId();
  return (
    <div className={styles.appearance}>
      <fieldset className={styles.fieldset} data-testid="theme-selector">
        <legend>Theme</legend>
        <div className={styles.themes}>
          {THEMES.map((option) => (
            <label
              key={option.value}
              className={styles.choice}
              data-selected={theme === option.value}
            >
              <ThemeSample theme={option.value} />
              <span className={styles.caption}>
                <input
                  type="radio"
                  name={`${id}-theme`}
                  value={option.value}
                  checked={theme === option.value}
                  aria-checked={theme === option.value}
                  onChange={() => onThemeChange(option.value)}
                />
                {option.label}
              </span>
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className={styles.fieldset} data-testid="density-selector">
        <legend>Card density</legend>
        <div className={styles.densities}>
          {DENSITIES.map((option) => (
            <label
              key={option.value}
              className={styles.choice}
              data-selected={density === option.value}
            >
              <div className={styles.densitySample} data-density={option.value} aria-hidden="true">
                <SampleHabits />
              </div>
              <span className={styles.caption}>
                <input
                  type="radio"
                  name={`${id}-density`}
                  value={option.value}
                  checked={density === option.value}
                  aria-checked={density === option.value}
                  onChange={() => onDensityChange(option.value)}
                />
                {option.label}
              </span>
            </label>
          ))}
        </div>
      </fieldset>
    </div>
  );
}
